import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useRef } from 'react';
import { toAccept } from '../../documents/constants';
import { PARSE_EXTENSIONS_BY_WORKFLOW } from '../../documents/parseWorkflow';

interface Props {
  onUpload: (file: File) => void;
  uploading: boolean;
  parsing: boolean;
}

/** Picks one certificate file. PDF only: it is the format insurance parsing reads. */
export const CertificateUpload = ({ onUpload, uploading, parsing }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clearing the value lets the same file be picked again after a refusal.
    e.target.value = '';
    if (file) onUpload(file);
  };

  return (
    <div className="flex flex-row items-center gap-3">
      <button
        type="button"
        className={classNames(
          'flex items-center gap-2 rounded-lg px-3.5 py-2 body-text-sb',
          'text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors enabled:cursor-pointer',
          { 'opacity-50 cursor-not-allowed': uploading }
        )}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        <ArrowUpTrayIcon className="size-5" aria-hidden="true" />
        {uploading ? 'Uploading…' : 'Upload certificate'}
      </button>
      <input
        ref={inputRef}
        type="file"
        hidden
        tabIndex={-1}
        aria-label="Insurance certificate file"
        accept={toAccept(PARSE_EXTENSIONS_BY_WORKFLOW['insurance-parse'])}
        onChange={handleChange}
      />
      <span role="status" className="body-text text-grey-400">
        {parsing && !uploading ? 'Reading policy details…' : ''}
      </span>
    </div>
  );
};
